import type { CSSProperties } from 'react'
import type { CertificateResult } from '@/services/certificateService'
import joscoworkLogo from '@/assets/logo.png'
import dlgmLogo from '@/assets/dlgm-logo.png'

// ── ข้อความและสีตามใบประกาศตัวอย่าง (โสภิดา ประดิษฐ์.pdf) — เฉพาะพื้นหลัง/กรอบเปลี่ยนเป็นโทนม่วง ──────────────────────────────────
const ISSUER_NAME = 'บริษัท ทำน้อยได้มาก จำกัด'
const INSTRUCTOR_TITLE = 'วิทยากร'
const INK = '#141414'
const NAME_BLUE = '#1F3FBA'
// กรอบเส้นคู่สีม่วง + พื้นหลังขาว→ลาเวนเดอร์อ่อนมาก (โทน JosCoWork: #6C63FF, #8B5CF6, #F3F1FF)
const BORDER = '#7A68D8'
const BORDER_SOFT = '#B7ACEB'
const BG_START = '#FFFFFF'
const BG_MID = '#F7F5FF'
const BG_END = '#F3F1FF'
const FONT = "'Sarabun', sans-serif"

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })

// "ระยะเวลารวม" = ผลรวมนาทีวิดีโอของทุกบทเรียนในคอร์ส (m_course.duration_minutes) — 0 = ไม่แสดงบรรทัดนี้
function formatTrainingDuration(minutes: number): string | null {
  const total = Math.round(minutes || 0)
  if (total <= 0) return null
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h > 0 && m > 0) return `${h} ชั่วโมง ${m} นาที`
  if (h > 0) return `${h} ชั่วโมง`
  return `${m} นาที`
}

// ขนาดตัวอักษรเป็น cqw (สัมพัทธ์กับความกว้างของใบประกาศเอง) — ย่อ/ขยายตามกรอบได้ทั้งในหน้า preview และตอนพิมพ์
// ชื่อผู้เรียนยิ่งยาวยิ่งลดขนาด แต่พยายามให้อยู่บรรทัดเดียว
function nameSize(name: string): string {
  const len = name.length
  const cqw = len > 40 ? 1.8 : len > 32 ? 2.1 : len > 24 ? 2.4 : 2.8   // ลดจากเดิม (2.1–3.5) กันชื่อล้นกรอบ
  return `${cqw}cqw`
}

function courseSize(title: string): string {
  const len = title.length
  const cqw = len > 80 ? 1.9 : len > 56 ? 2.2 : len > 40 ? 2.5 : 2.7
  return `${cqw}cqw`
}

// จัดข้อความกึ่งกลางที่ระยะ top (cqw) ตามตำแหน่งในใบประกาศตัวอย่าง (ค่ามาจากภาพหน้าเดียวของ PDF กว้าง 1302px → 1cqw = 13.02px)
const centered = (top: number, extra?: CSSProperties): CSSProperties => ({
  position: 'absolute',
  left: '50%',
  top: `${top}cqw`,
  transform: 'translate(-50%, -50%)',
  width: '92cqw',
  textAlign: 'center',
  lineHeight: 1.6,   // สูงพอให้สระ/วรรณยุกต์บน-ล่างแสดงครบ (เดิม 1.25 แล้วเสี่ยงโดนตัด)
  ...extra,
})

// ลวดลายพื้นหลัง — คลื่น/เส้นโค้งโปร่งแสงตามมุมและขอบ + วงกลมเล็ก + จุดประ (ไม่มีลูกศร/ภาพประกอบ)
// อยู่ชั้นล่างสุดและใช้ opacity ต่ำมาก กลางใบประกาศเว้นสะอาด ไม่ทับข้อความ/โลโก้ — viewBox 1414×1000 = อัตราส่วน A4 (297:210) เดียวกับใบประกาศ
function BackgroundArt() {
  const dotsTL = [101, 129, 157, 184].flatMap((x) => [135, 165, 195, 225, 254].map((y) => [x, y]))
  const dotsR = [1290, 1308, 1326, 1344].flatMap((x) => [486, 514, 542, 570, 598].map((y) => [x, y]))   // ชิดขอบขวา ไม่ทับชื่อหลักสูตรที่ยาวสุด
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1414 1000" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id="cert-wave-a" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#8B5CF6" stopOpacity="0.15" />
          <stop offset="1" stopColor="#6C63FF" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="cert-wave-b" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#6C63FF" stopOpacity="0.09" />
          <stop offset="1" stopColor="#8B5CF6" stopOpacity="0.03" />
        </linearGradient>
        <radialGradient id="cert-orb" cx="0.35" cy="0.3" r="0.8">
          <stop offset="0" stopColor="#8B5CF6" stopOpacity="0.20" />
          <stop offset="1" stopColor="#6C63FF" stopOpacity="0.06" />
        </radialGradient>
      </defs>

      {/* มุมซ้ายบน */}
      <path d="M0 262 C130 240 300 160 350 0 L0 0 Z" fill="url(#cert-wave-a)" />
      {/* มุมขวาบน (ซ้อน 2 ชั้น) */}
      <path d="M1030 0 C1060 200 1250 380 1414 430 L1414 0 Z" fill="url(#cert-wave-b)" />
      <path d="M1120 0 C1150 120 1270 230 1414 268 L1414 0 Z" fill="url(#cert-wave-a)" />
      {/* มุมซ้ายล่าง (ซ้อน 2 ชั้น) */}
      <path d="M0 430 C200 470 380 700 452 1000 L0 1000 Z" fill="url(#cert-wave-b)" />
      <path d="M0 660 C180 700 330 850 440 1000 L0 1000 Z" fill="url(#cert-wave-a)" />
      {/* มุมขวาล่าง — โค้งออกไปทางมุมเพื่อไม่ลงมาทับบล็อกลายเซ็นมากนัก */}
      <path d="M980 1000 C1150 985 1330 850 1414 700 L1414 1000 Z" fill="url(#cert-wave-a)" />
      <path d="M840 1000 C1040 980 1300 830 1414 760 L1414 1000 Z" fill="url(#cert-wave-b)" />

      {/* วงกลมเล็ก */}
      <circle cx="101" cy="342" r="27" fill="url(#cert-orb)" />
      <circle cx="1215" cy="300" r="39" fill="url(#cert-orb)" />
      <circle cx="422" cy="801" r="27" fill="url(#cert-orb)" />

      {/* จุดประเล็ก ๆ ตามมุม */}
      <g fill="#8B5CF6" fillOpacity="0.32">
        {dotsTL.map(([x, y]) => <circle key={`tl-${x}-${y}`} cx={x} cy={y} r="3" />)}
        {dotsR.map(([x, y]) => <circle key={`r-${x}-${y}`} cx={x} cy={y} r="3" />)}
      </g>
    </svg>
  )
}

// ใบประกาศ A4 แนวนอน (297:210) — จัดหน้าตามใบประกาศตัวอย่าง: พื้นขาว→ลาเวนเดอร์ + ลวดลายคลื่นโปร่งแสงตามมุม กรอบเส้นคู่สีม่วง
// โลโก้ทำน้อยได้มาก + โลโก้ JosCoWork ด้านบน ข้อความไทยกึ่งกลาง ลายเซ็นวิทยากรมุมขวาล่าง
// คงคลาส `certificate-print-area` และ aspect-ratio ไว้ เพราะ print CSS ใน index.css พึ่งพาอยู่ (พิมพ์หน้าเดียว A4 แนวนอน)
export default function CertificateCard({ certificate }: { certificate: CertificateResult }) {
  const instructorName = certificate.instructor
    ? `${certificate.instructor.first_name} ${certificate.instructor.last_name}`.trim()
    : null
  const signatureUrl = certificate.instructor?.signature_url || null
  const duration = formatTrainingDuration(certificate.duration_minutes)

  return (
    <div
      className="certificate-print-area relative mx-auto w-full overflow-hidden"
      style={{
        aspectRatio: '297 / 210',
        background: `linear-gradient(135deg, ${BG_START} 0%, ${BG_MID} 50%, ${BG_END} 100%)`,
        // container query context — หน่วย cqw ทั้งไฟล์อิงความกว้างจริงของใบประกาศ ไม่ว่าจะแสดงเล็กหรือเต็มหน้ากระดาษตอนพิมพ์
        containerType: 'inline-size',
        fontFamily: FONT,
        color: INK,
      }}
    >
      {/* กรอบเส้นคู่ (เส้นบางนอก + เส้นหนากลาง + เส้นบางใน) */}
      <div className="pointer-events-none absolute" style={{ inset: '2.05cqw', border: `0.1cqw solid ${BORDER_SOFT}` }} />
      <div className="pointer-events-none absolute" style={{ inset: '2.4cqw', border: `0.28cqw solid ${BORDER}` }} />
      <div className="pointer-events-none absolute" style={{ inset: '2.85cqw', border: `0.1cqw solid ${BORDER_SOFT}` }} />

      <BackgroundArt />

      {/* โลโก้: ทำน้อยได้มาก นำหน้า JosCoWork */}
      <div
        className="absolute flex items-center justify-center"
        style={{ left: '50%', top: '9.2cqw', transform: 'translate(-50%, -50%)', gap: '3cqw', height: '9cqw' }}
      >
        <img src={dlgmLogo} alt="Doing Less Getting More" style={{ height: '8cqw', width: 'auto' }} />
        <span style={{ width: '0.15cqw', height: '7cqw', background: `${BORDER}99` }} />
        <img src={joscoworkLogo} alt="JosCoWork" style={{ height: '9cqw', width: 'auto' }} />
      </div>

      <p style={centered(17.5, { fontSize: '2.6cqw', fontWeight: 700 })}>{ISSUER_NAME}</p>
      <p style={centered(23, { fontSize: '2.1cqw' })}>ขอมอบประกาศนียบัตรนี้เพื่อแสดงว่า</p>

      <p
        style={centered(28.4, {
          fontSize: nameSize(certificate.holder_name),
          fontWeight: 700,
          color: NAME_BLUE,
          whiteSpace: 'nowrap',
          // ไม่ใช้ overflow:hidden — จะตัดสระ/วรรณยุกต์บน-ล่างที่เกินกรอบบรรทัด (ขนาดฟอนต์ลดตามความยาวชื่ออยู่แล้ว)
          lineHeight: 2,
          padding: '0.5cqw 0',
        })}
      >
        {certificate.holder_name}
      </p>

      <p style={centered(35.2, { fontSize: '2.1cqw' })}>ได้ผ่านการฝึกอบรมหลักสูตร</p>
      <p style={centered(39.9, { fontSize: courseSize(certificate.course_title), fontWeight: 700, width: '80cqw' })}>
        {certificate.course_title}
      </p>

      <p style={centered(52, { fontSize: '2.2cqw', fontWeight: 700 })}>วันที่ {formatDate(certificate.issued_at)}</p>
      {duration && <p style={centered(55.8, { fontSize: '2.2cqw' })}>ระยะเวลารวม {duration}</p>}

      {/* วิทยากร (ลายเซ็น + เส้น + ชื่อ + ตำแหน่ง) มุมขวาล่าง — ไม่มีลายเซ็นในระบบ = เว้นที่ว่างเหนือเส้น (ไม่ใช้ลายเซ็นจำลอง) */}
      {instructorName && (
        <div className="absolute" style={{ left: '68cqw', top: '55cqw', width: '27cqw', textAlign: 'center' }}>
          <div style={{ height: '6.4cqw', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            {signatureUrl && (
              <img src={signatureUrl} alt={instructorName} style={{ maxHeight: '6.4cqw', maxWidth: '100%', objectFit: 'contain' }} />
            )}
          </div>
          <div style={{ borderTop: `0.1cqw solid ${INK}88`, marginTop: '0.4cqw' }} />
          <p style={{ marginTop: '0.9cqw', fontSize: '1.25cqw', fontWeight: 700, lineHeight: 1.3 }}>{instructorName}</p>
          <p style={{ marginTop: '0.5cqw', fontSize: '1.25cqw', fontWeight: 700, lineHeight: 1.3 }}>{INSTRUCTOR_TITLE}</p>
        </div>
      )}
    </div>
  )
}
